import type { NextRouter } from 'next/router';
import React, { useEffect } from 'react';
import { toOverviewUrl } from '@core/links/members';
import { isDefined } from '@peloton/types/isDefined';
import { toRelationshipInfo } from '@engage/members/MemberCard';
import type { Member } from '@engage/members/models';
import { isSelf } from '@engage/members/models';
import { loadMember } from '@engage/members/redux';
import { getMember } from '@engage/members/selectors';
import {
  getViewedMemberUsernameOrIdFromPath,
  toWorkoutHistoryPath,
} from '@engage/overview/urls';
import { useReduxAction, useReduxState } from '@engage/redux/hooks';
import { useFeatureToggle } from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import { toAchievementsUrl } from '@members/routes/achievements';
import { toGoalsUrl } from '@members/routes/goals';
import { getViewedMemberIdFromPath, isWorkoutsPath } from '@members/routes/workouts';
import type UserNavProps from './UserNavProps';
import UserNavView from './UserNavView';

const NOT_READY = 'notReady';

const isWorkoutsLinkActive = (router: NextRouter) => isWorkoutsPath(router.asPath);

export const useUserNav = (userId: string | undefined): UserNavProps | undefined => {
  const router = useRouter();
  const loadMemberAction = useReduxAction(loadMember);
  const currentUserId = userId ?? (getUserIdFromPath(router) as string);
  const member = useReduxState(getMember, currentUserId);
  const userNav = isDefined(member)
    ? toUserNav(member, currentUserId, router.asPath)
    : null;
  const simpleGoalsEnabled = useFeatureToggle(Feature.Simple_goals);

  useEffect(() => {
    if (
      isDefined(currentUserId) &&
      currentUserId !== NOT_READY &&
      (!isDefined(member) || currentUserId !== member.id)
    ) {
      loadMemberAction(currentUserId);
    }
  }, [currentUserId]);

  if (isDefined(userNav)) {
    return {
      ...userNav,
      isWorkoutsLinkActive: isWorkoutsLinkActive(router),
      simpleGoalsEnabled,
    };
  } else {
    return undefined;
  }
};
const UserNav: React.FC<React.PropsWithChildren<OwnProps>> = ownProps => {
  const userNav = useUserNav(ownProps.userId);

  if (!isDefined(userNav)) {
    return null;
  }

  return <UserNavView {...userNav} />;
};

const toUserNav = (member: Member, namespace: string, currentPath: string) => ({
  ...member,
  ...toRelationshipInfo(member),
  namespace,
  currentPath,
  overviewPath: toOverviewUrl(member.username, isSelf(member)),
  workoutsPath: toWorkoutHistoryPath(isSelf(member), member.id),
  achievementsPath: toAchievementsUrl(member.id, isSelf(member)),
  goalsPath: toGoalsUrl(member.id, isSelf(member)),
});

// TODO: move url logic to packages
const getUserIdFromPath = (router: NextRouter) => {
  let viewedMemberId = getViewedMemberIdFromPath(router);
  if (!viewedMemberId) {
    viewedMemberId = getViewedMemberUsernameOrIdFromPath(router);
  }
  return viewedMemberId ? viewedMemberId : NOT_READY;
};

type OwnProps = { userId?: string };

export default UserNav;
